import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { 
  Search,
  User,
  Loader2,
  ShoppingBag,
  Zap,
  CloudOff,
  Calendar
} from "lucide-react";
import { toast } from "sonner";

const OrdersPage = () => {
  const navigate = useNavigate();
  
  // Live Shopify search state
  const [liveSearchTerm, setLiveSearchTerm] = useState("");
  const [liveSearchResults, setLiveSearchResults] = useState([]);
  const [liveSearching, setLiveSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const searchTimeoutRef = useRef(null);

  // Live Shopify search function
  const searchShopifyLive = async (query) => {
    if (!query || query.length < 2) {
      setLiveSearchResults([]);
      setHasSearched(false);
      return;
    }
    
    setLiveSearching(true);
    setHasSearched(true);
    try {
      const response = await axios.get(`${API}/shopify/search-orders`, {
        params: { query, limit: 25 }
      });
      
      if (response.data.success) {
        setLiveSearchResults(response.data.orders);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.includes("not connected")) {
        toast.error("Shopify not connected. Please connect in Settings first.");
      } else {
        console.error("Live search failed:", error);
        toast.error("Search failed. Please try again.");
      }
      setLiveSearchResults([]);
    } finally {
      setLiveSearching(false);
    }
  };

  // Debounced live search
  const handleLiveSearchChange = (value) => {
    setLiveSearchTerm(value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    if (!value || value.length < 2) {
      setLiveSearchResults([]);
      setHasSearched(false);
      return;
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      searchShopifyLive(value);
    }, 500);
  };

  // Clear live search
  const clearLiveSearch = () => {
    setLiveSearchTerm("");
    setLiveSearchResults([]);
    setHasSearched(false);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Unfulfilled":
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-100">Unfulfilled</Badge>;
      case "Fulfilled":
        return <Badge className="bg-green-100 text-green-800 border-green-200 hover:bg-green-100">Fulfilled</Badge>;
      case "Waybill Created":
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-100">Waybill Created</Badge>;
      case "Partial":
        return <Badge className="bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-100">Partial</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getPaymentBadge = (status) => {
    return status === "Paid" || status === "paid" ? (
      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100">Paid</Badge>
    ) : (
      <Badge variant="outline">{status}</Badge>
    );
  };

  return (
    <div className="p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
          Find Shopify Order
        </h1>
        <p className="text-slate-500">Search for any order by number, customer name or email</p>
      </div>

      {/* Live Shopify Search */}
      <Card className="border-[#96BF48] border-2 shadow-lg bg-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Zap className="w-6 h-6 text-[#96BF48]" />
            <h3 className="font-semibold text-lg text-slate-800">Live Shopify Search</h3>
          </div>
          
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#96BF48]" />
            <Input
              placeholder="Enter order # (e.g. 6130), customer name, or email..."
              value={liveSearchTerm}
              onChange={(e) => handleLiveSearchChange(e.target.value)}
              className="pl-12 pr-24 h-14 text-lg border-2 border-slate-200 focus:border-[#96BF48] focus:ring-[#96BF48] rounded-xl"
              data-testid="live-shopify-search"
              autoFocus
            />
            {liveSearching && (
              <Loader2 className="absolute right-16 top-1/2 -translate-y-1/2 w-5 h-5 text-[#96BF48] animate-spin" />
            )}
            {liveSearchTerm && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearLiveSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                Clear
              </Button>
            )}
          </div>
          
          <p className="text-center text-xs text-slate-400 mt-3">
            Searches directly in your Shopify store
          </p>
        </CardContent>
      </Card>

      {/* Search Results */}
      {hasSearched && (
        <div className="mt-6">
          {liveSearching ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#96BF48]" />
              <span className="ml-3 text-slate-500">Searching Shopify...</span>
            </div>
          ) : liveSearchResults.length > 0 ? (
            <Card className="border-slate-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                <span className="text-sm font-medium text-slate-700">
                  Found {liveSearchResults.length} order(s)
                </span>
              </div>
              <div className="divide-y divide-slate-100">
                {liveSearchResults.map((order) => (
                  <div
                    key={order.shopify_order_id}
                    className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/quotes/request`, { 
                      state: { order, fromLiveSearch: true } 
                    })}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-6">
                        <div>
                          <div className="text-xl font-bold text-slate-900">{order.order_number}</div>
                          <div className="text-sm text-slate-500 flex items-center gap-1">
                            <ShoppingBag className="w-3 h-3" />
                            {order.line_items?.length || 0} item(s)
                          </div>
                        </div>
                        <div className="border-l border-slate-200 pl-6">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-slate-400" />
                            <span className="font-medium text-slate-800">{order.customer_name}</span>
                          </div>
                          <div className="text-sm text-slate-500">{order.customer_email}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <div className="text-lg font-semibold text-slate-900">
                            {order.currency} {order.total_amount?.toLocaleString()}
                          </div>
                          <div className="flex items-center gap-2 justify-end">
                            {getPaymentBadge(order.financial_status)}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {getStatusBadge(order.status)}
                          <span className="text-xs text-slate-400 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {order.created_at ? new Date(order.created_at).toLocaleDateString() : ''}
                          </span>
                        </div>
                        <Button
                          size="default"
                          className="bg-[#E8601C] hover:bg-[#D55015] text-white px-6"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/quotes/request`, { 
                              state: { order, fromLiveSearch: true } 
                            });
                          }}
                        >
                          Create Waybill
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ) : (
            <div className="text-center py-12">
              <CloudOff className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500 text-lg">No orders found for "{liveSearchTerm}"</p>
              <p className="text-slate-400 text-sm mt-1">Try a different search term</p>
            </div>
          )}
        </div>
      )}

      {/* Empty State - Before Search */}
      {!hasSearched && (
        <div className="mt-12 text-center">
          <ShoppingBag className="w-16 h-16 mx-auto text-slate-200 mb-4" />
          <p className="text-slate-400">Enter an order number or customer details above to search</p>
        </div>
      )}
    </div>
  );
};

export default OrdersPage;
